import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import BASE_URL from '../../config';
import { fetchWithAuth } from '../../utils/authUtils';
import { getLocalCart, addToLocalCart, removeFromLocalCart, clearLocalCart, replaceLocalCart } from '../../utils/localCart';
import { readErrorResponse } from '../../utils/readErrorResponse';

let localCartSyncInFlight = null;

const extractSyncErrorMessage = (error) => {
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    const messages = error.map(extractSyncErrorMessage).filter(Boolean);
    return messages[0] || 'Unknown sync error';
  }
  if (typeof error?.error === 'string') {
    return error.error;
  }
  if (typeof error?.detail === 'string') {
    return error.detail;
  }
  if (error?.message) {
    return error.message;
  }
  if (error && typeof error === 'object') {
    const firstStringValue = Object.values(error).find((value) => typeof value === 'string');
    if (firstStringValue) {
      return firstStringValue;
    }
  }
  return 'Unknown sync error';
};

const isHttpStatusFailure = (message) => /Request failed with status \d+/.test(message);

const isFatalSyncError = (error) => {
  const message = extractSyncErrorMessage(error);

  if (
    message.includes('No access or refresh token available')
    || message.includes('Please log in')
    || message.includes('Failed to refresh access token')
    || message.includes('Failed to refetch after token refresh')
  ) {
    return true;
  }

  if (
    message.includes('Failed to fetch')
    || message.includes('NetworkError')
    || message.includes('Load failed')
  ) {
    return true;
  }

  return !isHttpStatusFailure(message);
};

const normalizeServerCartProductId = (item) => item?.product ?? item?.product_id;

const buildSyncError = async (item, response) => {
  const details = await readErrorResponse(response);
  return {
    product: item.product,
    error: extractSyncErrorMessage(details),
    details,
    status: response.status,
  };
};

const performLocalCartSync = async (dispatch) => {
  const localItems = getLocalCart();
  if (localItems.length === 0) {
    return { remainingLocalItems: [], warnings: [], errors: [], syncedCount: 0, serverCartItemCount: 0, fatalError: null };
  }

  const warnings = [];
  const errors = [];
  const unsyncedItems = [];
  let syncedCount = 0;
  let fatalError = null;

  // Прямые API-вызовы, затем один fetchCart в конце, чтобы не делать лишние
  // промежуточные обновления store при массовой синхронизации.
  for (let index = 0; index < localItems.length; index += 1) {
    const item = localItems[index];

    if (item.access_type === 'free') {
      try {
        const response = await fetchWithAuth(`${BASE_URL}/products/v1/add-free-to-cart/${item.product}/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          const syncError = await buildSyncError(item, response);
          unsyncedItems.push({ ...item, sync_error: 'sync_failed' });
          errors.push(syncError);
          continue;
        }

        syncedCount += 1;
        continue;
      } catch (err) {
        const errorMessage = extractSyncErrorMessage(err);
        if (isFatalSyncError(err)) {
          fatalError = { product: item.product, error: errorMessage, code: 'fatal_sync_error' };
          unsyncedItems.push({ ...item, sync_error: 'fatal_sync_error' });
          unsyncedItems.push(...localItems.slice(index + 1));
          errors.push(fatalError);
          break;
        }
        unsyncedItems.push({ ...item, sync_error: 'sync_failed' });
        errors.push({ product: item.product, error: errorMessage });
        continue;
      }
    }

    if (!item.price_tier) {
      unsyncedItems.push({ ...item, sync_error: 'missing_price_tier' });
      warnings.push({ product: item.product, error: 'missing_price_tier' });
      continue;
    }

    try {
      const response = await fetchWithAuth(`${BASE_URL}/cart/v1/cart/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product: item.product,
          quantity: 1,
          price_tier: item.price_tier,
        }),
      });

      if (!response.ok) {
        const syncError = await buildSyncError(item, response);
        unsyncedItems.push({ ...item, sync_error: 'sync_failed' });
        errors.push(syncError);
        continue;
      }

      syncedCount += 1;
    } catch (err) {
      const errorMessage = extractSyncErrorMessage(err);
      if (isFatalSyncError(err)) {
        fatalError = { product: item.product, error: errorMessage, code: 'fatal_sync_error' };
        unsyncedItems.push({ ...item, sync_error: 'fatal_sync_error' });
        unsyncedItems.push(...localItems.slice(index + 1));
        errors.push(fatalError);
        break;
      }
      unsyncedItems.push({ ...item, sync_error: 'sync_failed' });
      errors.push({ product: item.product, error: errorMessage });
    }
  }

  let serverCart;
  if (!fatalError) {
    try {
      // Один fetchCart в конце — актуальное состояние без дублей
      serverCart = await dispatch(fetchCart()).unwrap();
    } catch (err) {
      fatalError = {
        error: extractSyncErrorMessage(err),
        code: 'fetch_cart_failed',
      };
      errors.push(fatalError);
    }
  }

  const serverProductIds = new Set(
    Array.isArray(serverCart?.items)
      ? serverCart.items
        .map((item) => normalizeServerCartProductId(item))
        .filter((productId) => productId !== undefined && productId !== null)
      : [],
  );

  const duplicateResolvedItems = [];
  const remainingLocalItems = [];
  for (const item of unsyncedItems) {
    if (serverProductIds.has(item.product)) {
      duplicateResolvedItems.push(item);
      continue;
    }
    remainingLocalItems.push(item);
  }

  if (duplicateResolvedItems.length > 0) {
    const duplicateProductIds = new Set(duplicateResolvedItems.map((item) => item.product));
    const filteredErrors = errors.filter((entry) => !duplicateProductIds.has(entry.product));
    errors.length = 0;
    errors.push(...filteredErrors);
    syncedCount += duplicateResolvedItems.length;
    warnings.push(
      ...duplicateResolvedItems.map((item) => ({
        product: item.product,
        error: 'already_in_server_cart',
      })),
    );
  }

  replaceLocalCart(remainingLocalItems);

  const serverCartItemCount = Array.isArray(serverCart?.items) ? serverCart.items.length : 0;

  return {
    remainingLocalItems,
    warnings,
    errors,
    syncedCount,
    serverCartItemCount,
    fatalError,
  };
};

// Thunks
export const postCartItem = createAsyncThunk(
  'cart/postCartItem',
  async ({ id, priceTier = 'full' }, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/cart/v1/cart/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product: id, price_tier: priceTier }),
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      const res = await response.json();

      return res
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addFreeToCart = createAsyncThunk(
  'cart/addFreeToCart',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/products/v1/add-free-to-cart/${productId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      const res = await response.json();
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCartItem = createAsyncThunk(
  'cart/deleteCartItem',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/cart/v1/cart/${id}/`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      // Обновляем тоталы в фоне (UI уже обновлён оптимистично в pending)
      dispatch(fetchCart());
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/cart/v1/cart/`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Clear cart on server (DELETE /cart/v1/cart/ without pk)
export const clearServerCart = createAsyncThunk(
  'cart/clearServerCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/cart/v1/cart/`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Sync local (guest) cart to server after login
export const syncLocalCartToServer = createAsyncThunk(
  'cart/syncLocalCartToServer',
  async (_, { dispatch, rejectWithValue }) => {
    if (!localCartSyncInFlight) {
      localCartSyncInFlight = performLocalCartSync(dispatch).finally(() => {
        localCartSyncInFlight = null;
      });
    }

    try {
      const result = await localCartSyncInFlight;
      if (result.fatalError) {
        return rejectWithValue(result);
      }
      return result;
    } catch (error) {
      return rejectWithValue(error?.message || error);
    }
  }
);

// Initial State
const initialState = {
  cart: {
    id: null,
    is_active: false,
    items: [], 
    total_price: 0,
    total_discounted_price: 0,
    total_discount_amount: 0,
    user_discount_percentage: 0,
    user: null,
  },
  localItems: getLocalCart(),
  postCartItemStatus: 'idle',
  postCartItemError: null,
  deleteCartItemStatus: 'idle',
  deleteCartItemError: null,
  fetchCartStatus: 'idle',
  fetchCartError: null,
  paymentClearing: false,
};

// Slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      state.cart = { ...initialState.cart };
    },
    setPaymentClearing: (state, action) => {
      state.paymentClearing = action.payload;
    },
    addLocalItem: (state, action) => {
      const product = action.payload;
      const updated = addToLocalCart(product);
      state.localItems = updated;
    },
    removeLocalItem: (state, action) => {
      const id = action.payload;
      const updated = removeFromLocalCart(id);
      state.localItems = updated;
    },
    clearLocalItems: (state) => {
      clearLocalCart();
      state.localItems = [];
    },
    refreshLocalItems: (state) => {
      state.localItems = getLocalCart();
    },
  },
  extraReducers: (builder) => {
    // Post Cart Item
    builder
    .addCase(postCartItem.pending, (state) => {
      state.postCartItemStatus = 'loading';
    })
    .addCase(postCartItem.fulfilled, (state, action) => {
      state.postCartItemStatus = 'succeeded';
      state.cart = action.payload;
    })
    .addCase(postCartItem.rejected, (state, action) => {
      state.postCartItemStatus = 'failed';
      state.postCartItemError = action.payload
    });

  // Delete Cart Item (оптимистичное удаление — UI обновляется мгновенно)
  builder
    .addCase(deleteCartItem.pending, (state, action) => {
      state.deleteCartItemStatus = 'loading';
      // Оптимистично убираем товар из UI сразу
      const idToRemove = action.meta.arg;
      state.cart.items = state.cart.items.filter(item => item.id !== idToRemove);
    })
    .addCase(deleteCartItem.fulfilled, (state) => {
      state.deleteCartItemStatus = 'succeeded';
    })
    .addCase(deleteCartItem.rejected, (state, action) => {
      state.deleteCartItemStatus = 'failed';
      state.deleteCartItemError = action.payload;
      // Если товар не найден на сервере (404/stale) — убираем из UI
      if (action.meta?.arg) {
        state.cart.items = state.cart.items.filter(item => item.id !== action.meta.arg);
      }
    });

  // Fetch Cart
  builder
    .addCase(fetchCart.pending, (state) => {
      state.fetchCartStatus = 'loading';
    })
    .addCase(fetchCart.fulfilled, (state, action) => {
      state.fetchCartStatus = 'succeeded';
      // Не перезаписываем очищенную корзину пока идёт подтверждение оплаты
      if (!state.paymentClearing) {
        state.cart = action.payload;
      }
    })
    .addCase(fetchCart.rejected, (state, action) => {
      state.fetchCartStatus = 'failed';
      state.fetchCartError = action.payload
    });

  // Clear server cart
  builder
    .addCase(clearServerCart.fulfilled, (state, action) => {
      state.cart = action.payload;
    })
    .addCase(clearServerCart.rejected, (state) => {
      // Even if server clear fails, keep local state cleared
      state.cart.items = [];
    });

  // Sync local cart to server
  builder
    .addCase(syncLocalCartToServer.fulfilled, (state, action) => {
      state.localItems = action.payload?.remainingLocalItems || [];
    })
    .addCase(syncLocalCartToServer.rejected, (state, action) => {
      state.localItems = action.payload?.remainingLocalItems || getLocalCart();
    });

  // Add Free To Cart
  builder
    .addCase(addFreeToCart.pending, (state) => {
      state.postCartItemStatus = 'loading';
    })
    .addCase(addFreeToCart.fulfilled, (state, action) => {
      state.postCartItemStatus = 'succeeded';
      // Обновляем корзину целиком, если бек вернул
      if (action.payload && action.payload.cart) {
        state.cart = action.payload.cart;
      } else if (action.payload && action.payload.cart_total !== undefined) {
        // Фоллбек: обновим суммы
        state.cart.total_price = action.payload.cart_total;
        state.cart.total_discounted_price = state.cart.total_price;
      }
    })
    .addCase(addFreeToCart.rejected, (state, action) => {
      state.postCartItemStatus = 'failed';
      state.postCartItemError = action.payload
    });
},
});

export const { clearCart, setPaymentClearing, addLocalItem, removeLocalItem, clearLocalItems, refreshLocalItems } = cartSlice.actions;
export default cartSlice.reducer;
